import { gsap } from 'gsap';
import SplitText from 'gsap/SplitText';

import { hasAccentedCharacters } from './constants';

// Shared headline animation used by both the page-load timeline (Hero.tsx)
// and the per-slide carousel transition (useHeroCarousel.ts).
//
// Returns the tween/timeline plus the SplitText instance (or null) so the
// caller can manage cleanup. Appends animations to `parentTl` at `position`.

type SingleLineMode = 'chars' | 'scale-fade';

type Opts = {
  parentTl: GSAPTimeline;     // Timeline to append the animation to
  position?: string | number; // Position label on parentTl
  wrapEl: HTMLElement;        // .headline-wrap container
  headlineEl: HTMLElement;    // <h1> inside wrap
  gDur: number;               // Base duration
  ease?: string;
  singleLineMode?: SingleLineMode;
};

type Result = {
  split: SplitText | null;
  isSingleLine: boolean;
};

export function animateHeadlineIn({
  parentTl,
  position = 0,
  wrapEl,
  headlineEl,
  gDur,
  ease = 'expo.inOut',
  singleLineMode = 'chars',
}: Opts): Result {

  const headlineDur = gDur * 1.25;
  const text = headlineEl.textContent || '';

  // ---------- Accent bypass: skip SplitText for accented characters
  if (hasAccentedCharacters(text)) {
    parentTl.fromTo(wrapEl,
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: headlineDur, ease },
      position
    );
    return { split: null, isSingleLine: false };
  }

  // ---------- Split on lines first
  const splitLines = new SplitText(headlineEl, {
    type: 'lines',
    mask: 'lines',
    linesClass: 'line',
  });

  // ---------- Multi-line: scale wrap + alternating per-line y-offset
  if (splitLines.lines.length > 1) {
    parentTl.fromTo(wrapEl,
      { scale: 0.5 },
      { scale: 1, duration: headlineDur, ease },
      position
    );
    splitLines.lines.forEach((line, i) => {
      const offset = (i % 2 === 0) ? '150%' : '-150%';
      parentTl.fromTo(line,
        { y: offset },
        { y: 0, duration: headlineDur, ease },
        position
      );
    });
    return { split: splitLines, isSingleLine: false };
  }

  // ---------- Single line: discard the lines split, choose fallback
  splitLines.revert();

  if (singleLineMode === 'scale-fade') {
    parentTl.fromTo(wrapEl,
      { scale: 0.6, opacity: 0 },
      { scale: 1, opacity: 1, duration: headlineDur, ease },
      position
    );
    return { split: null, isSingleLine: true };
  }

  // Single-line `chars` mode: split into characters and stagger in from below
  const splitChars = new SplitText(headlineEl, {
    type: 'chars',
    mask: 'chars',
    charsClass: 'char',
  });

  splitChars.chars.forEach((char, i) => {
    parentTl.fromTo(char,
      { y: '120%' },
      { y: 0, duration: headlineDur, ease, delay: i * 0.016 },
      position
    );
  });

  return { split: splitChars, isSingleLine: true };
}
